import { useMemo, useState } from "react";
import client from "../api/client";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const styles = {
  page: {
    fontSize: 11,
    padding: 0,
  },

  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    marginBottom: 10,
    background: "#fff",
  },

  header: (bg) => ({
    background: bg,
    color: "#fff",
    padding: "8px 12px",
  }),

  title: {
    fontSize: 12,
    fontWeight: 700,
    margin: 0,
  },

  sub: {
    fontSize: 10,
    opacity: 0.85,
  },

  body: {
    padding: 10,
  },

  label: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 3,
    color: "#495057",
  },

  input: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
  },

  btn: {
    height: 30,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
  },

  statCard: {
    borderRadius: 10,
    padding: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
    height: "100%",
  },

  statTitle: {
    fontSize: 10,
    color: "#6c757d",
    marginBottom: 3,
  },

  statValue: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0d6efd",
  },

  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
  },

  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

const fmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const get = (obj, pascal, camel) => obj?.[pascal] ?? obj?.[camel] ?? "";

export default function GstReportsPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gstr1, setGstr1] = useState([]);
  const [gstr3b, setGstr3b] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReports = async () => {
    if (!fromDate || !toDate) return alert("Select from and to date");

    try {
      setLoading(true);

      const [r1, r3b] = await Promise.all([
        client.get(`/gst-reports/gstr1?fromDate=${fromDate}&toDate=${toDate}`),
        client.get(`/gst-reports/gstr3b?fromDate=${fromDate}&toDate=${toDate}`),
      ]);

      setGstr1(Array.isArray(r1.data) ? r1.data : []);
      setGstr3b(r3b.data || null);
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to load GST reports");
    } finally {
      setLoading(false);
    }
  };

  const monthlyTrend = useMemo(() => {
    const map = {};

    gstr1.forEach((r) => {
      const rawDate = get(r, "InvoiceDate", "invoiceDate");
      const date = rawDate ? new Date(rawDate) : null;

      if (!date || Number.isNaN(date.getTime())) return;

      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0",
      )}`;

      if (!map[key]) {
        map[key] = {
          month: key,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalTax: 0,
        };
      }

      const cgst = Number(get(r, "CGST", "cgst") || 0);
      const sgst = Number(get(r, "SGST", "sgst") || 0);
      const igst = Number(get(r, "IGST", "igst") || 0);

      map[key].cgst += cgst;
      map[key].sgst += sgst;
      map[key].igst += igst;
      map[key].totalTax += cgst + sgst + igst;
    });

    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [gstr1]);

  const totals = useMemo(() => {
    return {
      taxable: gstr1.reduce(
        (s, r) => s + Number(get(r, "TaxableValue", "taxableValue") || 0),
        0,
      ),

      cgst: gstr1.reduce((s, r) => s + Number(get(r, "CGST", "cgst") || 0), 0),

      sgst: gstr1.reduce((s, r) => s + Number(get(r, "SGST", "sgst") || 0), 0),

      igst: gstr1.reduce((s, r) => s + Number(get(r, "IGST", "igst") || 0), 0),

      invoice: gstr1.reduce(
        (s, r) => s + Number(get(r, "InvoiceValue", "invoiceValue") || 0),
        0,
      ),
    };
  }, [gstr1]);

  const exportGstr1Excel = () => {
    if (gstr1.length === 0) return alert("No GSTR-1 data");

    const ws = XLSX.utils.json_to_sheet(gstr1);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "GSTR-1");

    XLSX.writeFile(wb, `GSTR1_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      {/* FILTER */}
      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg, #22052D, #CCB3D1)")}>
          <h6 style={styles.title}>
            <i className="bi bi-receipt me-2"></i>
            GST Reports
          </h6>

          <div style={styles.sub}>GSTR-1, GSTR-3B, GST trends and exports</div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label style={styles.label}>From Date</label>

              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label style={styles.label}>To Date</label>

              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-6 d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                style={styles.btn}
                onClick={loadReports}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>

              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={exportGstr1Excel}
              >
                <i className="bi bi-file-earmark-excel me-1"></i>
                Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="row g-2 mb-2">
        {[
          ["Taxable", totals.taxable],
          ["CGST", totals.cgst],
          ["SGST", totals.sgst],
          ["IGST", totals.igst],
          ["Invoice", totals.invoice],
        ].map(([label, value]) => (
          <div className="col-md-2" key={label}>
            <div style={styles.statCard}>
              <div style={styles.statTitle}>{label}</div>

              <div style={styles.statValue}>₹ {fmt(value)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* GSTR 3B */}
      {gstr3b && (
        <div style={styles.card}>
          <div style={styles.header("linear-gradient(90deg,#198754,#157347)")}>
            <h6 style={styles.title}>
              <i className="bi bi-calculator me-2"></i>
              GSTR-3B Summary
            </h6>
          </div>

          <div style={styles.body}>
            <table className="table table-sm table-bordered mb-0">
              <tbody>
                {[
                  [
                    "Outward Taxable",
                    get(gstr3b, "OutwardTaxableValue", "outwardTaxableValue"),
                  ],
                  ["Output CGST", get(gstr3b, "OutputCGST", "outputCGST")],
                  ["Output SGST", get(gstr3b, "OutputSGST", "outputSGST")],
                  ["Output IGST", get(gstr3b, "OutputIGST", "outputIGST")],
                  [
                    "Net Tax Payable",
                    get(gstr3b, "NetTaxPayable", "netTaxPayable"),
                  ],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <th style={styles.th}>{k}</th>

                    <td className="text-end" style={styles.td}>
                      ₹ {fmt(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CHART */}
      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#0dcaf0,#0aa2c0)")}>
          <h6 style={styles.title}>
            <i className="bi bi-bar-chart me-2"></i>
            Monthly GST Trend
          </h6>
        </div>

        <div style={{ ...styles.body, height: 300 }}>
          {monthlyTrend.length === 0 ? (
            <div className="text-center text-muted mt-5">No chart data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend />

                <Bar dataKey="cgst" name="CGST" />
                <Bar dataKey="sgst" name="SGST" />
                <Bar dataKey="igst" name="IGST" />
                <Bar dataKey="totalTax" name="Total GST" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div style={styles.card}>
        <div style={styles.header("linear-gradient(90deg,#198754,#157347)")}>
          <h6 style={styles.title}>
            <i className="bi bi-table me-2"></i>
            GSTR-1 Outward Supplies
          </h6>
        </div>

        <div style={styles.body}>
          <div
            style={{
              overflowX: "auto",
              maxHeight: 350,
            }}
          >
            <table className="table table-hover table-bordered table-sm mb-0">
              <thead className="sticky-top" style={{ zIndex: 1 }}>
                <tr>
                  {[
                    "Invoice",
                    "Date",
                    "Customer",
                    "GSTIN",
                    "Supply",
                    "Taxable",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Value",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {gstr1.length ? (
                  gstr1.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {get(r, "InvoiceNo", "invoiceNo")}
                      </td>

                      <td style={styles.td}>
                        {get(r, "InvoiceDate", "invoiceDate")}
                      </td>

                      <td style={styles.td}>
                        {get(r, "CustomerName", "customerName")}
                      </td>

                      <td style={styles.td}>
                        {get(r, "GSTNo", "gstNo") || "-"}
                      </td>

                      <td style={styles.td}>
                        {get(r, "SupplyType", "supplyType")}
                      </td>

                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "TaxableValue", "taxableValue"))}
                      </td>

                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "CGST", "cgst"))}
                      </td>

                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "SGST", "sgst"))}
                      </td>

                      <td className="text-end" style={styles.td}>
                        {fmt(get(r, "IGST", "igst"))}
                      </td>

                      <td className="text-end fw-semibold" style={styles.td}>
                        {fmt(get(r, "InvoiceValue", "invoiceValue"))}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="10"
                      className="text-center text-muted"
                      style={{ padding: 18 }}
                    >
                      No GST records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
